'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import Modal from '@/components/ui/Modal';

interface Props {
  open: boolean;
  onClose: () => void;
}

interface PostFormData {
  title: string;
  client: string;
  page: string;
  platform: string;
  postType: string;
  copy: string;
  scheduledDate: string;
  scheduledTime: string;
  approvalRoute: string;
}

const clients = ['Bravo Coffee', 'Nexus Tech', 'Verde Organic', 'Luxe Living', 'UrbanWear Co.', 'FitTrack Pro', 'Solaris Solar', 'PetPal Supply'];

const pagesByClient: Record<string, string[]> = {
  'Bravo Coffee': ['Bravo Coffee Official', 'Bravo Coffee IG'],
  'Nexus Tech': ['Nexus Technology', 'Nexus Tech IG'],
  'Verde Organic': ['Verde Organic Foods', 'Verde Organic IG'],
  'Luxe Living': ['Luxe Living Interiors', 'Luxe Living IG'],
  'UrbanWear Co.': ['UrbanWear Co.'],
  'FitTrack Pro': ['FitTrack Pro'],
  'Solaris Solar': ['Solaris Solar Energy'],
  'PetPal Supply': ['PetPal Supply'],
};

export default function CreatePostModal({ open, onClose }: Props) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const { register, handleSubmit, watch, formState: { errors } } = useForm<PostFormData>({
    defaultValues: { platform: 'instagram', postType: 'Image', approvalRoute: 'manager' },
  });

  const selectedClient = watch('client');
  const pages = selectedClient ? pagesByClient[selectedClient] || [] : [];

  const onSubmit = async () => {
    setIsSubmitting(true);
    // Backend integration point: POST /api/posts/schedule
    await new Promise((r) => setTimeout(r, 1200));
    toast.success('Post scheduled successfully — pending manager approval');
    setIsSubmitting(false);
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} title="Schedule New Post" subtitle="Plan and schedule content for your client pages" size="lg">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="p-6 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="label-base">Post Title</label>
              <p className="helper-text">Internal reference name — not shown in the post</p>
              <input
                type="text"
                placeholder="e.g. Back to School Lookbook — Week 2"
                className={`input-base mt-1 ${errors.title ? 'border-negative' : ''}`}
                {...register('title', { required: 'Post title is required' })}
              />
              {errors.title && <p className="error-text">{errors.title.message}</p>}
            </div>

            <div>
              <label className="label-base">Client</label>
              <select className={`input-base ${errors.client ? 'border-negative' : ''}`} {...register('client', { required: 'Select a client' })}>
                <option value="">Select client…</option>
                {clients.map((c) => <option key={`create-client-${c}`} value={c}>{c}</option>)}
              </select>
              {errors.client && <p className="error-text">{errors.client.message}</p>}
            </div>

            <div>
              <label className="label-base">Page</label>
              <select className="input-base" {...register('page', { required: true })} disabled={!selectedClient}>
                <option value="">Select page…</option>
                {pages.map((p) => <option key={`page-opt-${p}`} value={p}>{p}</option>)}
              </select>
            </div>

            <div>
              <label className="label-base">Platform</label>
              <div className="flex gap-2 mt-1">
                {['facebook', 'instagram'].map((p) => (
                  <label key={`platform-radio-${p}`} className="flex items-center gap-2 flex-1 p-3 rounded-lg border border-border cursor-pointer hover:border-primary/40 has-[:checked]:border-primary has-[:checked]:bg-primary/5 transition-all">
                    <input type="radio" value={p} {...register('platform')} className="accent-primary" />
                    <span className="text-sm font-medium capitalize">{p}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="label-base">Post Type</label>
              <select className="input-base" {...register('postType')}>
                <option value="Image">Image</option>
                <option value="Video">Video</option>
                <option value="Carousel">Carousel</option>
                <option value="Story">Story</option>
                <option value="Reel">Reel</option>
              </select>
            </div>

            <div>
              <label className="label-base">Scheduled Date</label>
              <input type="date" className="input-base" {...register('scheduledDate', { required: true })} />
            </div>

            <div>
              <label className="label-base">Scheduled Time</label>
              <input type="time" className="input-base" {...register('scheduledTime', { required: true })} />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="label-base mb-0">Post Copy</label>
              <span className={`text-xs tabular-nums ${charCount > 2200 ? 'text-negative' : charCount > 1800 ? 'text-warning' : 'text-muted-foreground'}`}>
                {charCount} / 2,200
              </span>
            </div>
            <p className="helper-text">Caption text that will appear with your post. First 125 chars shown without 'See more'.</p>
            <textarea
              rows={4}
              placeholder="Write your post copy here… include relevant hashtags and emojis 🎯"
              className={`input-base resize-none mt-1 ${errors.copy ? 'border-negative' : ''}`}
              {...register('copy', {
                required: 'Post copy is required',
                onChange: (e) => setCharCount(e.target.value.length),
              })}
            />
            {errors.copy && <p className="error-text">{errors.copy.message}</p>}
          </div>

          {/* Media Upload */}
          <div>
            <label className="label-base">Media</label>
            <p className="helper-text">Upload image or video. JPG, PNG, MP4 — max 30MB per file.</p>
            <div className="mt-1 border-2 border-dashed border-border rounded-xl p-6 text-center hover:border-primary/40 hover:bg-primary/[0.02] transition-all duration-150 cursor-pointer">
              <p className="text-sm font-medium text-foreground">Drop media here or click to browse</p>
              <p className="text-xs text-muted-foreground mt-1">Feed: 1080×1080px · Story/Reel: 1080×1920px</p>
              {/* Backend integration point: media upload */}
            </div>
          </div>

          <div>
            <label className="label-base">Approval Routing</label>
            <p className="helper-text">Who needs to approve this post before it publishes?</p>
            <select className="input-base mt-1" {...register('approvalRoute')}>
              <option value="manager">Route to assigned Manager</option>
              <option value="admin">Route to Admin for approval</option>
              <option value="auto">Auto-approve — publish immediately</option>
            </select>
          </div>
        </div>

        <div className="flex items-center justify-between px-6 py-4 border-t border-border">
          <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => toast.info('Saved as draft')} className="btn-secondary">
              Save as Draft
            </button>
            <button type="submit" disabled={isSubmitting} className="btn-primary">
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Scheduling…
                </span>
              ) : (
                'Schedule Post'
              )}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
}